import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Heart, Loader2 } from 'lucide-react';
import { settingsApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Wrapper für öffentliche/Mitglieder-Seiten: prüft den Wartungsmodus-Status
 * und zeigt ggf. eine schlanke Wartungsseite. Admins/Super-Admins werden
 * bewusst durchgelassen, damit das Aussperren des Betreibers ausgeschlossen ist.
 *
 * Usage:
 *   <PublicMaintenanceGate>
 *     <PublicSeminarsPage />
 *   </PublicMaintenanceGate>
 */
export default function PublicMaintenanceGate({ children }) {
    const [state, setState] = useState({ loading: true, enabled: false, title: '', message: '' });
    const { user } = useAuth() || {};
    const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');

    useEffect(() => {
        settingsApi.getMaintenance()
            .then((r) => setState({ loading: false, enabled: !!r.data.enabled, title: r.data.title || '', message: r.data.message || '' }))
            .catch(() => setState({ loading: false, enabled: false, title: '', message: '' }));
    }, []);

    if (state.loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }
    // Admin-Rollen werden niemals ausgeschlossen – sie kommen so an Admin-
    // und Mitglieder-Bereich (Impersonation) heran, auch während der Wartung.
    if (!state.enabled || isAdmin) return children;

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="maintenance-screen">
            <header className="bg-secondary">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-center gap-3">
                    <div className="p-2.5 bg-primary rounded-xl">
                        <Heart className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-white">BGPR-Akademie</h1>
                    </div>
                </div>
            </header>
            <main className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="max-w-xl w-full bg-white rounded-2xl border border-gray-100 p-8 sm:p-10 text-center shadow-sm">
                    <div className="mx-auto mb-5 p-3 bg-amber-100 text-amber-700 rounded-full w-fit">
                        <Wrench className="h-7 w-7" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-3" data-testid="maintenance-title">{state.title || 'Kurze Wartungspause'}</h2>
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line" data-testid="maintenance-message">
                        {state.message}
                    </p>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-sm">
                        <Link to="/login" className="px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/80 font-semibold" data-testid="maintenance-admin-link">
                            Admin-Bereich
                        </Link>
                    </div>
                </div>
            </main>
            <footer className="bg-secondary py-5">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center text-xs text-gray-500">
                    Berliner Gesellschaft für Prävention und Rehabilitation von Herz-Kreislauferkrankungen e.V.
                </div>
            </footer>
        </div>
    );
}
